import {
  buildForest,
  type Capabilities,
  flattenForest,
  NO_CAPABILITIES,
  type PermissionGrantRecord,
  type PermissionLevel,
  type PermissionResourceType,
  type PrincipalType,
  type TeamRecord,
  toTeamNode,
  type UserRecord,
} from "@contract-manager/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import type { MessageKey } from "../i18n/messages";
import type { Translate } from "../i18n/translate";
import { Alert } from "./Alert";

/**
 * Who may see and who may change one folder or one contract.
 *
 * The screen states the rule it is editing, because an access list on its own
 * does not say who else is already in. Nothing here is open to the
 * organisation: a resource with no grants is reachable by whoever uploaded or
 * created it and by the admin team, and everyone else has to be named.
 */

/** What each level is called; its hint spells out what it permits. */
function levelLabel(level: PermissionLevel): MessageKey {
  return `permissions.level.${level}`;
}

/** UPLOAD only means anything for a folder. */
function levelsFor(
  resourceType: PermissionResourceType,
): readonly PermissionLevel[] {
  return resourceType === "FOLDER"
    ? ["METADATA", "UPLOAD", "VIEW", "EDIT"]
    : ["METADATA", "VIEW", "EDIT"];
}

export function PermissionEditor({
  api,
  resourceType,
  resourceId,
  onCallerAccess,
}: {
  api: ApiClient;
  resourceType: PermissionResourceType;
  resourceId: string;
  /**
   * What the caller may do here, each time it is (re)loaded. The page around
   * this editor needs the same answer to decide what else to offer, and
   * asking the API twice for it would be two requests for one fact.
   */
  onCallerAccess?: (access: Capabilities) => void;
}) {
  const auth = useAuth();
  // Held in a ref so a caller passing an inline function does not make `load`
  // change identity on every render — that would refetch in a loop.
  const reportCallerAccess = useRef(onCallerAccess);
  reportCallerAccess.current = onCallerAccess;
  const { t } = useI18n();
  const [grants, setGrants] = useState<PermissionGrantRecord[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [principalType, setPrincipalType] = useState<PrincipalType>("TEAM");
  const [principalId, setPrincipalId] = useState("");
  const [level, setLevel] = useState<PermissionLevel>("VIEW");
  const [callerAccess, setCallerAccess] =
    useState<Capabilities>(NO_CAPABILITIES);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const report = useCallback(
    (caught: unknown): void => {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    },
    [auth, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [permissions, userList, teamList] = await Promise.all([
        api.listPermissions(resourceType, resourceId),
        api.listUsers(),
        api.listTeams(),
      ]);
      setGrants(permissions.items);
      setCallerAccess(permissions.callerAccess);
      reportCallerAccess.current?.(permissions.callerAccess);
      setUsers(userList.items);
      setTeams(teamList.items);
      setError(null);
    } catch (caught) {
      report(caught);
    } finally {
      setLoading(false);
    }
  }, [api, report, resourceId, resourceType]);

  useEffect(() => {
    void load();
  }, [load]);

  const grant = async () => {
    if (principalId === "") {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.grantPermission(resourceType, resourceId, {
        principalType,
        principalId,
        level,
      });
      setPrincipalId("");
      await load();
    } catch (caught) {
      report(caught);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (target: PermissionGrantRecord) => {
    setSaving(true);
    setError(null);
    try {
      await api.revokePermission(
        resourceType,
        resourceId,
        target.principalType,
        target.principalId,
      );
      await load();
    } catch (caught) {
      report(caught);
    } finally {
      setSaving(false);
    }
  };

  const mayEdit = callerAccess.canEdit;
  const alwaysIn = t(`permissions.alwaysIn.${resourceType}`);
  const teamOptions = flattenForest(buildForest(teams.map(toTeamNode)));
  const candidates =
    principalType === "TEAM"
      ? teamOptions.map((team) => ({
          id: team.id,
          label: `${"　".repeat(team.depth)}${team.name}`,
        }))
      : users.map((user) => ({
          id: user.email,
          label:
            user.displayName === null
              ? user.email
              : t("permissions.userWithEmail", {
                  name: user.displayName,
                  email: user.email,
                }),
        }));

  return (
    <section className="ds-rows --gap-small app-permissions">
      <h3 className="ds-text --medium-bold">{t("permissions.title")}</h3>

      {error !== null && <Alert tone="failed">{error}</Alert>}

      {loading && (
        <p className="ds-text --small --muted">{t("users.loading")}</p>
      )}

      {!loading && (
        <p className="ds-text --small --muted">
          {grants.length === 0
            ? t("permissions.none", { alwaysIn })
            : t("permissions.some", { alwaysIn })}
        </p>
      )}

      {grants.length > 0 && (
        <ul className="app-list">
          {grants.map((item) => (
            <li
              key={`${item.principalType}#${item.principalId}`}
              className="ds-cols --middle --nowrap --gap-small"
            >
              <span className="app-truncate app-grow">
                {nameOf(item, users, teams, t)}
              </span>
              <span className="ds-text --small --muted">
                {t(levelLabel(item.level))}
              </span>
              {mayEdit && (
                <button
                  type="button"
                  className="ds-button --xsmall --alert"
                  disabled={saving}
                  onClick={() => void revoke(item)}
                >
                  <span>{t("common.delete")}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!loading && !mayEdit && (
        <p className="ds-text --small --muted">{t("permissions.needEdit")}</p>
      )}

      {mayEdit && (
        <div className="ds-rows --gap-xsmall">
          <div className="ds-cols --middle --gap-small">
            <select
              className="ds-select"
              value={principalType}
              disabled={saving}
              aria-label={t("permissions.principalType")}
              onChange={(event) => {
                setPrincipalType(event.target.value as PrincipalType);
                setPrincipalId("");
              }}
            >
              <option value="TEAM">
                {t("permissions.principalType.TEAM")}
              </option>
              <option value="USER">
                {t("permissions.principalType.USER")}
              </option>
            </select>

            <select
              className="ds-select app-grow"
              value={principalId}
              disabled={saving}
              aria-label={t("permissions.principal")}
              onChange={(event) => setPrincipalId(event.target.value)}
            >
              <option value="">{t("permissions.choose")}</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.label}
                </option>
              ))}
            </select>

            <select
              className="ds-select"
              value={level}
              disabled={saving}
              aria-label={t("permissions.level")}
              onChange={(event) =>
                setLevel(event.target.value as PermissionLevel)
              }
            >
              {levelsFor(resourceType).map((candidate) => (
                <option key={candidate} value={candidate}>
                  {t(levelLabel(candidate))}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="ds-button --primary --icon-plus"
              disabled={saving || principalId === ""}
              onClick={() => void grant()}
            >
              <span>{t("common.add")}</span>
            </button>
          </div>

          <p className="ds-text --small --muted">
            {t(`permissions.hint.${level}`)}
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * A grant stores an id, not a name. Falling back to the id rather than hiding
 * the row keeps a grant to a team that was since renamed — or to someone whose
 * directory row has not loaded — visible and removable.
 */
function nameOf(
  grant: PermissionGrantRecord,
  users: readonly UserRecord[],
  teams: readonly TeamRecord[],
  t: Translate<MessageKey>,
): string {
  if (grant.principalType === "TEAM") {
    const team = teams.find(
      (candidate) => candidate.teamId === grant.principalId,
    );
    return team === undefined
      ? grant.principalId
      : t("permissions.teamName", { name: team.name });
  }
  const user = users.find((candidate) => candidate.email === grant.principalId);
  return user?.displayName === undefined || user.displayName === null
    ? grant.principalId
    : t("permissions.userWithEmail", {
        name: user.displayName,
        email: grant.principalId,
      });
}
